import logo from "@/assets/img/FacilityAI_Logo.svg";
import { acceptInvitation, getInvitationByToken } from "@/services/user-service";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import {
	Button,
	Card,
	Divider,
	Form,
	Input,
	Layout,
	Result,
	Spin,
	Typography,
	message,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const { Content, Footer } = Layout;
const { Text } = Typography;

interface InvitationDetails {
	email: string;
	organization: {
		fullName: string;
		full_name?: string;
	};
	role: {
		name: string;
	};
	expiresAt: string;
}

const AcceptInvitation = () => {
	const { token } = useParams<{ token: string }>();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	useEffect(() => {
		const fetchInvitation = async () => {
			if (!token) {
				setError("Invalid invitation link");
				setLoading(false);
				return;
			}

			try {
				const data = await getInvitationByToken(token);
				setInvitation(data);
			} catch (err: any) {
				setError(err.message || "Failed to load invitation");
			} finally {
				setLoading(false);
			}
		};

		fetchInvitation();
	}, [token]);

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			setSubmitting(true);

			await acceptInvitation(token!, values.name, values.password);
			setSuccess(true);
		} catch (err: any) {
			if (err?.errorFields) return;
			message.error(err.message || "Failed to create account");
		} finally {
			setSubmitting(false);
		}
	};

	const orgName =
		invitation?.organization?.fullName ||
		invitation?.organization?.full_name ||
		"the organization";

	const renderContent = () => {
		if (loading) {
			return (
				<div style={{ textAlign: "center", padding: "4vh 0" }}>
					<Spin size="large" />
					<Text
						style={{ display: "block", marginTop: 16 }}
						type="secondary"
					>
						Loading invitation...
					</Text>
				</div>
			);
		}

		if (success) {
			return (
				<Result
					status="success"
					title="Account Created!"
					subTitle={`You can now sign in with ${invitation?.email}.`}
					extra={[
						<Button
							type="primary"
							key="login"
							onClick={() => navigate("/login")}
						>
							Go to Login
						</Button>,
					]}
				/>
			);
		}

		if (error && !invitation) {
			return (
				<Result
					status="error"
					title="Invitation Error"
					subTitle={error}
					extra={[
						<Button
							type="primary"
							key="login"
							onClick={() => navigate("/login")}
						>
							Go to Login
						</Button>,
					]}
				/>
			);
		}

		return (
			<>
				<div style={{ textAlign: "center", marginBottom: "2vh" }}>
					<Text style={{ fontSize: "1.1rem" }}>
						You've been invited to join <strong>{orgName}</strong>
					</Text>
					<br />
					<Text type="secondary" style={{ fontSize: "0.9rem" }}>
						as a {invitation?.role?.name?.toLowerCase() || "user"}
					</Text>
				</div>

				<Form layout="vertical" form={form} onFinish={handleSubmit}>
					<Form.Item label="Email">
						<Input value={invitation?.email} disabled />
					</Form.Item>

					<Form.Item
						name="name"
						label="Your Name"
						rules={[
							{ required: true, message: "Please enter your name" },
							{ min: 2, message: "Name must be at least 2 characters" },
						]}
					>
						<Input
							placeholder="Full Name"
							prefix={<UserOutlined />}
						/>
					</Form.Item>

					<Form.Item
						name="password"
						label="Create Password"
						rules={[
							{ required: true, message: "Please create a password" },
							{
								min: 6,
								message: "Password must be at least 6 characters",
							},
						]}
					>
						<Input.Password
							placeholder="Password"
							prefix={<LockOutlined />}
						/>
					</Form.Item>

					<Form.Item
						name="confirmPassword"
						label="Confirm Password"
						dependencies={["password"]}
						rules={[
							{ required: true, message: "Please confirm your password" },
							({ getFieldValue }) => ({
								validator(_, value) {
									if (!value || getFieldValue("password") === value) {
										return Promise.resolve();
									}
									return Promise.reject(
										new Error("Passwords do not match"),
									);
								},
							}),
						]}
					>
						<Input.Password
							placeholder="Confirm Password"
							prefix={<LockOutlined />}
						/>
					</Form.Item>

					<Form.Item style={{ marginTop: "4vh" }}>
						<Button
							type="primary"
							htmlType="submit"
							block
							loading={submitting}
							disabled={submitting}
						>
							Create Account
						</Button>
					</Form.Item>
				</Form>

				<Divider>Already have an account?</Divider>
				<Button
					type="default"
					block
					onClick={() => navigate("/login")}
					disabled={submitting}
				>
					Sign In
				</Button>
			</>
		);
	};

	return (
		<Layout style={{ minHeight: "100dvh" }}>
			<Content
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: "5vh 2vw",
				}}
			>
				<Card style={{ width: "90vw", maxWidth: "50rem", padding: "4vh 2vw" }}>
					<div style={{ textAlign: "center", marginBottom: "4vh" }}>
						<img
							src={logo}
							alt="Facility AI Logo"
							style={{
								maxHeight: "20vh",
								width: "auto",
								display: "block",
								margin: "0 auto",
							}}
						/>
					</div>
					{renderContent()}
				</Card>
			</Content>
			<Footer
				style={{ textAlign: "center", fontSize: "1.5vh", padding: "2vh 0" }}
			>
				&copy; {new Date().getFullYear()} FacilityAI, LLC
			</Footer>
		</Layout>
	);
};

export { AcceptInvitation };
